import { JSX, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@heroui/react';

interface NavDropdownProps {
  label: string;
  icon: ReactNode;
  items: NavDropdownItem[];
}

export interface NavDropdownItem {
  to: string;
  label: string;
  icon: ReactNode;
  onClick?: () => void;
  activeMatch?: string;
}

export function NavDropdown({ icon, items, label }: NavDropdownProps): JSX.Element {
  const { pathname } = useLocation();

  const isGroupActive = items.some(item => {
    const match = item.activeMatch ?? item.to;
    return pathname.startsWith(match);
  });

  const selectedClass = 'text-foreground nav-tab-active';
  const unselectedClass = 'text-muted hover:text-foreground';

  return (
    <Dropdown
      placement="bottom-start"
      classNames={{
        content: 'bg-surface-elevated',
      }}
    >
      <DropdownTrigger>
        <button
          type="button"
          className={`inline-flex items-center px-6 pt-4 pb-5 text-sm font-medium transition-colors ${isGroupActive ? selectedClass : unselectedClass}`}
        >
          <span className="h-5 w-5 mr-1">{icon}</span>
          {label}
          <ChevronDownIcon className="h-3 w-3 ml-1" />
        </button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label={`${label} navigation`}
        classNames={{
          base: 'bg-surface-elevated',
        }}
      >
        {items.map(item => {
          const match = item.activeMatch ?? item.to;
          const isActive = pathname.startsWith(match);

          return (
            <DropdownItem
              key={item.to}
              className={isActive ? 'text-foreground bg-primary/10' : ''}
              startContent={<span className="h-5 w-5">{item.icon}</span>}
              onPress={() => item.onClick?.()}
              textValue={item.label}
            >
              <Link to={item.to} className="block w-full">
                {item.label}
              </Link>
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );
}
